"use client";

import { FormEvent, useEffect, useState } from "react";
import Link from "next/link";
import { MessageCircle, Send, Clock } from "lucide-react";

interface CommentItem {
  id: string;
  userName: string;
  content: string;
  createdAt: string;
}

function timeAgo(iso: string): string {
  const date = new Date(iso);
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60) return "przed chwilą";
  if (diff < 3600) return `${Math.floor(diff / 60)} min temu`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} godz. temu`;
  if (diff < 30 * 86400) return `${Math.floor(diff / 86400)} dni temu`;
  return date.toLocaleDateString("pl-PL");
}

export default function Comments({ slug }: { slug: string }) {
  const [items, setItems] = useState<CommentItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [content, setContent] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "ok" | "err">("idle");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    fetch(`/api/blog/posts/${slug}/comments`).then((r) => r.json()).then((d) => {
      setItems(Array.isArray(d) ? d : []);
      setLoaded(true);
    });
    fetch("/api/auth/me").then((r) => setAuthed(r.ok)).catch(() => setAuthed(false));
  }, [slug]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (content.trim().length < 3) { setErr("Komentarz za krótki"); return; }
    setState("loading"); setErr(null);
    const res = await fetch(`/api/blog/posts/${slug}/comments`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content: content.trim() }),
    });
    if (res.ok) {
      setState("ok");
      setContent("");
    } else {
      const data = await res.json().catch(() => ({}));
      setErr(data?.error || "Coś poszło nie tak");
      setState("err");
    }
  };

  return (
    <section className="mt-12 border-t border-slate-200 pt-10">
      <div className="flex items-center gap-2 mb-6">
        <MessageCircle className="w-5 h-5 text-blue-600" />
        <h2 className="text-xl font-bold text-slate-900">Komentarze ({items.length})</h2>
      </div>

      {/* Form */}
      {authed === false ? (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 mb-8 flex items-center justify-between gap-4 flex-wrap">
          <p className="text-sm text-slate-700">Aby dodać komentarz, musisz być zalogowany.</p>
          <Link href="/platforma/login" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
            Zaloguj się
          </Link>
        </div>
      ) : authed === true ? (
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-5 mb-8 shadow-sm">
          {state === "ok" ? (
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
              Dziękujemy! Twój komentarz oczekuje na moderację.
            </div>
          ) : (
            <>
              <textarea
                rows={3}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Napisz coś..."
                className="w-full p-3 rounded-lg border border-slate-200 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 text-sm resize-none"
                maxLength={4000}
              />
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-slate-400">{content.length}/4000</span>
                <button
                  type="submit"
                  disabled={state === "loading"}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-60"
                >
                  <Send className="w-4 h-4" />
                  {state === "loading" ? "Wysyłanie..." : "Wyślij"}
                </button>
              </div>
              {err && <p className="text-xs text-red-600 mt-2">{err}</p>}
            </>
          )}
        </form>
      ) : null}

      {/* List */}
      {!loaded ? (
        <p className="text-sm text-slate-400">Wczytywanie...</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-500">Brak komentarzy — bądź pierwszy!</p>
      ) : (
        <ul className="space-y-4">
          {items.map((c) => (
            <li key={c.id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 text-sm font-bold flex items-center justify-center">
                  {(c.userName || "?").charAt(0).toUpperCase()}
                </div>
                <span className="text-sm font-semibold text-slate-800">{c.userName || "Użytkownik"}</span>
                <span className="ml-auto text-xs text-slate-400 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {timeAgo(c.createdAt)}
                </span>
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed pl-10">
                {c.content}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
